import React, { useState, useEffect, useRef, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from './lib/supabase';
import { PayPalButtons, usePayPalScriptReducer } from '@paypal/react-paypal-js';
import { formatPrice } from './utils/priceFormatter';
import { getPayPalError } from './utils/paypalErrorHandler';
import LanguageContext from './LanguageContext';
import { translateService } from './translations';
import './BookSession.css';

function BookSession() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { language } = useContext(LanguageContext);
  
  const [service, setService] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [userNotes, setUserNotes] = useState('');
  const [bookedSlots, setBookedSlots] = useState([]); // Track booked time slots
  const [isCheckingAvailability, setIsCheckingAvailability] = useState(false);
  const [availableTimeSlots, setAvailableTimeSlots] = useState([]); // Available time slots from admin availability
  const [isLoadingAvailability, setIsLoadingAvailability] = useState(false);
  
  // Customer information fields
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [showPayPal, setShowPayPal] = useState(false);
  const [paymentApproved, setPaymentApproved] = useState(false);
  const [{ isPending, isResolved, isRejected }] = usePayPalScriptReducer();
  const [paypalButtonsReady, setPaypalButtonsReady] = useState(false);
  const [canRenderButtons, setCanRenderButtons] = useState(false);
  const paypalButtonContainerRef = useRef(null);

  // Debug PayPal script loading
  useEffect(() => {
    if (showPayPal) {
      console.log('PayPal Script Status:', { isPending, isResolved, isRejected });
      console.log('window.paypal:', window.paypal);
      console.log('window.paypal.Buttons:', window.paypal?.Buttons);
      console.log('paypalButtonsReady:', paypalButtonsReady);
      console.log('canRenderButtons:', canRenderButtons);
      console.log('Should show buttons?', canRenderButtons && showPayPal);
    }
  }, [showPayPal, isPending, isResolved, isRejected, paypalButtonsReady, canRenderButtons]);

  // Check for PayPal Buttons availability continuously
  useEffect(() => {
    // Check immediately if PayPal is already available
    const checkPayPal = () => {
      if (window.paypal && window.paypal.Buttons) {
        if (!paypalButtonsReady) {
          console.log('✅ PayPal Buttons detected and ready!');
          setPaypalButtonsReady(true);
        }
        if (!canRenderButtons) {
          console.log('✅ Setting canRenderButtons to true');
          setCanRenderButtons(true);
        }
        return true;
      }
      return false;
    };

    // Check immediately
    checkPayPal();

    // Poll every 500ms until PayPal is ready
    const interval = setInterval(() => {
      checkPayPal();
    }, 500);

    // Cleanup after 10 seconds
    const timeout = setTimeout(() => {
      clearInterval(interval);
    }, 10000);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [paypalButtonsReady, canRenderButtons]);

  // Render PayPal buttons directly when available
  useEffect(() => {
    if (showPayPal && canRenderButtons && window.paypal && window.paypal.Buttons && paypalButtonContainerRef.current) {
      // Clear container first
      paypalButtonContainerRef.current.innerHTML = '';
      
      console.log('🎨 Rendering PayPal buttons directly using window.paypal.Buttons...');
      
      try {
        window.paypal.Buttons({
          createOrder: async (data, actions) => {
            console.log('Creating PayPal order...');
            console.log('service:', service);
            console.log('service.price:', service?.price);
            
            if (!service || !service.price) {
              const errorMsg = 'Service information is missing. Please refresh the page.';
              setError(errorMsg);
              throw new Error(errorMsg);
            }
            
            const price = extractPrice(service.price);
            console.log('extracted price:', price, 'type:', typeof price);
            
            // Ensure price is a number
            const numericPrice = typeof price === 'number' && !isNaN(price) ? price : parseFloat(String(price)) || 0;
            
            if (numericPrice <= 0 || isNaN(numericPrice) || !isFinite(numericPrice)) {
              const errorMsg = `Invalid price amount: ${service.price}`;
              setError(errorMsg);
              throw new Error(errorMsg);
            }
            
            // Create order client-side using PayPal SDK
            // Use translated service for PayPal description
            const displayServiceForPayPal = service ? translateService(service, language) : service;
            return actions.order.create({
              purchase_units: [{
                description: `${displayServiceForPayPal.name} - ${displayServiceForPayPal.type} Session`,
                amount: {
                  value: numericPrice.toFixed(2),
                  currency_code: "USD"
                }
              }],
              application_context: {
                brand_name: "ManaWave",
                landing_page: "BILLING",
                user_action: "PAY_NOW"
              },
              intent: "CAPTURE"
            });
          },
          onApprove: handlePaymentApprove,
          onError: handlePaymentError,
          onCancel: handlePaymentCancel,
          style: {
            layout: "vertical",
            color: "blue",
            shape: "rect",
            label: "paypal"
          }
        }).render(paypalButtonContainerRef.current);
        
        console.log('✅ PayPal buttons rendered successfully!');
      } catch (error) {
        console.error('❌ Error rendering PayPal buttons:', error);
        setError('Failed to render PayPal buttons. Please refresh the page.');
      }
    }
    
    return () => {
      // Cleanup: clear container when component unmounts or dependencies change
      if (paypalButtonContainerRef.current) {
        paypalButtonContainerRef.current.innerHTML = '';
      }
    };
  }, [showPayPal, canRenderButtons, service]);

  // Wait for PayPal Buttons to be available when showing PayPal
  useEffect(() => {
    if (showPayPal && !paypalButtonsReady) {
      // Check if Buttons is available immediately (even if script hasn't resolved yet)
      if (window.paypal && window.paypal.Buttons) {
        console.log('✅ PayPal Buttons is now available!');
        setPaypalButtonsReady(true);
        return;
      }

      // Check for script errors
      const paypalScript = document.querySelector('script[src*="paypal.com/sdk/js"]');
      if (paypalScript) {
        paypalScript.onerror = () => {
          console.error('❌ PayPal script failed to load!');
          setError('Failed to load PayPal. Please check your internet connection and try again.');
        };
      }

      // Poll for Buttons to become available (max 15 seconds)
      let attempts = 0;
      const maxAttempts = 30; // 30 attempts * 500ms = 15 seconds
      
      const checkInterval = setInterval(() => {
        attempts++;
        
        // Check if window.paypal exists at all
        if (!window.paypal) {
          console.log(`⏳ Waiting for window.paypal... (attempt ${attempts}/${maxAttempts})`);
          
          // Try to check if script has loaded
          const scripts = document.querySelectorAll('script[src*="paypal.com/sdk/js"]');
          if (scripts.length > 0) {
            const script = scripts[scripts.length - 1];
            console.log('   Script loaded:', script.src);
            console.log('   Script readyState:', script.readyState);
            console.log('   Script onload:', script.onload ? 'has handler' : 'no handler');
          }
        } else {
          console.log(`✅ window.paypal exists! Checking for Buttons... (attempt ${attempts}/${maxAttempts})`);
          console.log('   window.paypal keys:', Object.keys(window.paypal));
          
          if (window.paypal.Buttons) {
            console.log('✅ PayPal Buttons is now available!');
            setPaypalButtonsReady(true);
            clearInterval(checkInterval);
          } else {
            console.log('   ⚠️ window.paypal exists but Buttons is not available yet');
          }
        }
        
        if (attempts >= maxAttempts) {
          console.error('❌ PayPal Buttons failed to load after 15 seconds');
          console.error('   window.paypal:', window.paypal);
          console.error('   Check browser console for PayPal script errors');
          setError('PayPal failed to initialize. Please refresh the page or check your browser console for errors.');
          clearInterval(checkInterval);
        }
      }, 500);

      return () => clearInterval(checkInterval);
    }
  }, [isResolved, showPayPal, paypalButtonsReady]);

  // Reset paypalButtonsReady when showPayPal changes
  useEffect(() => {
    if (!showPayPal) {
      setPaypalButtonsReady(false);
    }
  }, [showPayPal]);

  // Check if user is logged in
  const isLoggedIn = localStorage.getItem('userLoggedIn') === 'true';
  const userId = localStorage.getItem('userId');
  const userEmail = localStorage.getItem('userEmail');
  const userName = localStorage.getItem('userName');

  useEffect(() => {
    fetchServiceDetails();
  }, [id]);

  // Get translated service based on current language
  const translatedService = service ? translateService(service, language) : null;

  useEffect(() => {
    // Pre-fill customer info if logged in
    if (isLoggedIn) {
      setCustomerName(userName || '');
      setCustomerEmail(userEmail || '');
    }
  }, [isLoggedIn, userName, userEmail]);

  useEffect(() => {
    // Check availability when date is selected
    if (selectedDate) {
      fetchAdminAvailability();
      checkAvailability();
    } else {
      setAvailableTimeSlots([]);
      setSelectedTime(null);
    }
  }, [selectedDate, id]);

  const fetchServiceDetails = async () => {
    try {
      setIsLoading(true);
      const { data, error: fetchError } = await supabase
        .from('services')
        .select('*')
        .eq('id', id)
        .single();

      if (fetchError) {
        throw fetchError;
      }

      if (data) {
        setService(data);
      } else {
        setError('Service not found');
      }
    } catch (err) {
      console.error('Error fetching service:', err);
      setError('Failed to load service details. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch admin availability for selected date
  const fetchAdminAvailability = async () => {
    if (!selectedDate) return;
    
    try {
      setIsLoadingAvailability(true);
      const dateString = selectedDate.toISOString().split('T')[0];
      const dayOfWeek = selectedDate.getDay(); // 0 = Sunday, 1 = Monday, etc.

      // First, check for specific date override
      const { data: dateOverride, error: dateError } = await supabase
        .from('admin_date_availability')
        .select('*')
        .eq('availability_date', dateString)
        .single();

      let timeSlots = [];

      if (dateError && dateError.code !== 'PGRST116') { // PGRST116 = no rows returned
        console.error('Error fetching date availability:', dateError);
      }

      if (dateOverride) {
        // Use calendar override if exists
        if (dateOverride.is_available && dateOverride.time_slots && dateOverride.time_slots.length > 0) {
          timeSlots = generateTimeSlotsFromRanges(dateOverride.time_slots);
        }
        // If is_available is false, timeSlots remains empty (unavailable)
      } else {
        // No date override, use weekly schedule
        const { data: weeklySchedule, error: weeklyError } = await supabase
          .from('admin_weekly_schedule')
          .select('*')
          .eq('day_of_week', dayOfWeek)
          .eq('is_active', true)
          .single();

        if (weeklyError && weeklyError.code !== 'PGRST116') {
          console.error('Error fetching weekly schedule:', weeklyError);
        }

        if (weeklySchedule && weeklySchedule.time_slots && weeklySchedule.time_slots.length > 0) {
          timeSlots = generateTimeSlotsFromRanges(weeklySchedule.time_slots);
        }
      }

      setAvailableTimeSlots(timeSlots);
    } catch (err) {
      console.error('Error fetching admin availability:', err);
      setAvailableTimeSlots([]);
    } finally {
      setIsLoadingAvailability(false);
    }
  };

  // Generate time slots from time ranges (e.g., [{start: "09:00", end: "12:00"}])
  const generateTimeSlotsFromRanges = (ranges) => {
    const slots = [];
    
    ranges.forEach(range => {
      const start = parseTime(range.start);
      const end = parseTime(range.end);
      
      if (!start || !end) return;
      
      // Generate hourly slots
      let current = new Date(start);
      const endTime = new Date(end);
      
      while (current < endTime) {
        const timeString = formatTimeSlot(current);
        slots.push(timeString);
        // Add 1 hour
        current.setHours(current.getHours() + 1);
      }
    });
    
    // Remove duplicates and sort
    return [...new Set(slots)].sort((a, b) => {
      const timeA = parseTimeSlot(a);
      const timeB = parseTimeSlot(b);
      return timeA - timeB;
    });
  };

  // Parse time string (HH:MM) to Date object
  const parseTime = (timeString) => {
    if (!timeString) return null;
    const [hours, minutes] = timeString.split(':').map(Number);
    if (isNaN(hours) || isNaN(minutes)) return null;
    const date = new Date();
    date.setHours(hours, minutes, 0, 0);
    return date;
  };

  // Format time slot to display format (e.g., "9:00 AM")
  const formatTimeSlot = (date) => {
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    const displayMinutes = minutes.toString().padStart(2, '0');
    return `${displayHours}:${displayMinutes} ${ampm}`;
  };

  // Parse time slot string back to Date for sorting
  const parseTimeSlot = (timeString) => {
    const [time, ampm] = timeString.split(' ');
    const [hours, minutes] = time.split(':').map(Number);
    let hour24 = hours;
    if (ampm === 'PM' && hours !== 12) hour24 += 12;
    if (ampm === 'AM' && hours === 12) hour24 = 0;
    return hour24 * 60 + minutes; // Return minutes for easy comparison
  };

  const checkAvailability = async () => {
    if (!selectedDate || !id) return;
    
    try {
      setIsCheckingAvailability(true);
      const dateString = selectedDate.toISOString().split('T')[0]; // Format: YYYY-MM-DD
      
      const { data, error } = await supabase
        .from('bookings')
        .select('booking_time')
        .eq('service_id', id)
        .eq('booking_date', dateString)
        .in('status', ['pending', 'confirmed']); // Only check active bookings

      if (error) {
        console.error('Error checking availability:', error);
        return;
      }

      // Extract booked times
      const bookedTimes = (data || []).map(booking => booking.booking_time);
      setBookedSlots(bookedTimes);
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setIsCheckingAvailability(false);
    }
  };

  // Generate next 30 days for calendar and filter by availability
  const [availableDates, setAvailableDates] = useState([]);

  useEffect(() => {
    const loadAvailableDates = async () => {
      try {
        const dates = [];
        const today = new Date();
        
        // Fetch all weekly schedules and date overrides
        const [weeklyResult, dateResult] = await Promise.all([
          supabase.from('admin_weekly_schedule').select('*').eq('is_active', true),
          supabase.from('admin_date_availability').select('*')
        ]);

        if (weeklyResult.error) {
          console.error('Error fetching weekly schedules:', weeklyResult.error);
        }
        if (dateResult.error) {
          console.error('Error fetching date overrides:', dateResult.error);
        }

        const weeklySchedules = weeklyResult.data || [];
        const dateOverrides = dateResult.data || [];

        const weeklyScheduleMap = {};
        weeklySchedules.forEach(schedule => {
          weeklyScheduleMap[schedule.day_of_week] = schedule;
        });

        const dateOverrideMap = {};
        dateOverrides.forEach(override => {
          dateOverrideMap[override.availability_date] = override;
        });

        // Generate next 30 days
        for (let i = 1; i <= 30; i++) {
          const date = new Date(today);
          date.setDate(today.getDate() + i);
          const dateString = date.toISOString().split('T')[0];
          const dayOfWeek = date.getDay();

          // Check if date is available
          let isAvailable = false;

          // First check date override
          if (dateOverrideMap[dateString]) {
            isAvailable = dateOverrideMap[dateString].is_available && 
                         dateOverrideMap[dateString].time_slots?.length > 0;
          } else {
            // Check weekly schedule
            const weeklySchedule = weeklyScheduleMap[dayOfWeek];
            isAvailable = weeklySchedule && 
                         weeklySchedule.time_slots?.length > 0;
          }

          if (isAvailable) {
            dates.push(date);
          }
        }

        setAvailableDates(dates);
      } catch (err) {
        console.error('Error loading available dates:', err);
        // Fallback: show all dates if there's an error
        const dates = [];
        const today = new Date();
        for (let i = 1; i <= 30; i++) {
          const date = new Date(today);
          date.setDate(today.getDate() + i);
          dates.push(date);
        }
        setAvailableDates(dates);
      }
    };

    loadAvailableDates();
  }, []);

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const isFormValid = selectedDate && selectedTime && customerName.trim() && customerEmail.trim() && customerPhone.trim();

  // Extract numeric price from service.price (handles formats like "$50", "50 USD", "50", etc.)
  const extractPrice = (priceString) => {
    if (!priceString) return 0;
    // Remove all non-numeric characters except decimal point
    const numericString = priceString.replace(/[^0-9.]/g, '');
    return parseFloat(numericString) || 0;
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    setError('');
    
    if (!isFormValid) {
      setError('Please fill in all required fields');
      return;
    }

    // Check if slot is still available
    if (bookedSlots.includes(selectedTime)) {
      setError('This time slot has already been booked. Please select another time.');
      return;
    }

    // Show PayPal payment section
    setShowPayPal(true);
  };

  const createBooking = async (paymentId = null, paymentStatus = 'pending') => {
    try {
      setIsSubmitting(true);
      const dateString = selectedDate.toISOString().split('T')[0]; // Format: YYYY-MM-DD

      // Note: payment_id and payment_status fields must exist in the bookings table
      // Run add_payment_fields.sql in Supabase SQL Editor if these fields don't exist
      const bookingData = {
        user_id: userId || null,
        service_id: id,
        booking_date: dateString,
        booking_time: selectedTime,
        user_notes: userNotes,
        customer_name: customerName.trim(),
        customer_email: customerEmail.trim(),
        customer_phone: customerPhone.trim(),
        service_name: service.name,
        service_type: service.type,
        service_duration: service.duration,
        service_price: service.price,
        status: paymentStatus === 'completed' ? 'confirmed' : 'pending',
        payment_id: paymentId,
        payment_status: paymentStatus
      };

      const { data, error: insertError } = await supabase
        .from('bookings')
        .insert([bookingData])
        .select()
        .single();

      if (insertError) {
        throw insertError;
      }

      // Navigate to confirmation page with booking data
      navigate('/booking-confirmation', { 
        state: {
          service_name: service.name,
          service_type: service.type,
          service_duration: service.duration,
          service_price: service.price,
          booking_date: dateString,
          date: formatDate(selectedDate),
          booking_time: selectedTime,
          time: selectedTime,
          customer_name: customerName.trim(),
          customer_email: customerEmail.trim(),
          customer_phone: customerPhone.trim(),
          user_notes: userNotes,
          bookingId: data.id,
          paymentId: paymentId,
          paymentStatus: paymentStatus,
          // Also include camelCase versions for compatibility
          serviceName: service.name,
          serviceType: service.type,
          duration: service.duration,
          price: service.price,
          customerName: customerName.trim(),
          customerEmail: customerEmail.trim(),
          customerPhone: customerPhone.trim(),
          userNotes: userNotes
        }
      });
    } catch (err) {
      console.error('Error creating booking:', err);
      setError(err.message || 'Failed to create booking. Please try again.');
      setIsSubmitting(false);
    }
  };

  const handlePaymentApprove = async (data, actions) => {
    try {
      // Capture the payment client-side using PayPal SDK
      const order = await actions.order.capture();
      setPaymentApproved(true);
      
      // Payment successful
      console.log('Payment captured:', order);
      
      // Extract capture ID from the order
      const captureId = order.purchase_units[0]?.payments?.captures?.[0]?.id || order.id;
      
      // Create booking with payment confirmation
      await createBooking(captureId, 'completed');
    } catch (err) {
      console.error('Payment error:', err);
      setError(err.message || 'Payment processing failed. Please try again.');
      setPaymentApproved(false);
      setIsSubmitting(false);
    }
  };

  const handlePaymentError = (err) => {
    console.error('PayPal error:', err);
    const { message, keepPayPalVisible } = getPayPalError(err);
    setError(message);
    if (!keepPayPalVisible) setShowPayPal(false);
  };

  const handlePaymentCancel = () => {
    setShowPayPal(false);
    setError('Payment was cancelled. Please try again when ready.');
  };


  if (isLoading) {
    return (
      <div className="book-session-page">
        <div className="container" style={{ padding: '4rem 1rem', textAlign: 'center' }}>
          <div className="loading-spinner" style={{ 
            width: '50px', 
            height: '50px', 
            border: '4px solid rgba(124, 58, 237, 0.3)',
            borderTopColor: '#7C3AED',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 1rem'
          }}></div>
          <p>Loading service details...</p>
        </div>
      </div>
    );
  }

  if (error && !service) {
    return (
      <div className="book-session-page">
        <div className="container" style={{ padding: '4rem 1rem', textAlign: 'center' }}>
          <p style={{ color: '#ef4444', fontSize: '1.2rem' }}>{error}</p>
          <button 
            onClick={() => navigate('/services')}
            style={{
              marginTop: '1rem',
              padding: '0.75rem 1.5rem',
              background: '#7C3AED',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer'
            }}
          >
            Back to Services
          </button>
        </div>
      </div>
    );
    }

  if (!service) return null;

  // Use translated service for display
  const displayService = translatedService || service;

  return (
    <div className="book-session-page">
      {/* Session Overview */}
      <section className="session-overview">
        <div className="container">
          <div className="overview-content">
            <h1 className="session-service-name">{displayService.name}</h1>
            <div className="session-type-label">{displayService.type} Session</div>
            <div className="session-meta">
              <span className="meta-item">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM13 17H11V15H13V17ZM13 13H11V7H13V13Z" fill="currentColor"/>
                </svg>
                {displayService.duration}
              </span>
              <span className="meta-item">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM12 17C11.45 17 11 16.55 11 16C11 15.45 11.45 15 12 15C12.55 15 13 15.45 13 16C13 16.55 12.55 17 12 17ZM13 13H11V7H13V13Z" fill="currentColor"/>
                </svg>
                {formatPrice(displayService.price)}
              </span>
            </div>
          </div>
        </div>
        <div className="wave-divider-overview"></div>
      </section>

      {/* Booking Form */}
      <form className="booking-form" onSubmit={handleFormSubmit}>
        {/* Booking Details */}
        <section className="booking-details-section">
          <div className="container">
            <h2 className="section-title">Select Date & Time</h2>
            
            {/* Calendar View */}
            <div className="calendar-section">
              <div className="calendar-grid">
                {availableDates.map((date, index) => {
                  const isSelected = selectedDate && 
                    date.toDateString() === selectedDate.toDateString();
                  return (
                    <button
                      key={index}
                      type="button"
                      className={`date-button ${isSelected ? 'selected' : ''}`}
                      onClick={() => setSelectedDate(date)}
                    >
                      <span className="date-day">{date.getDate()}</span>
                      <span className="date-month">{date.toLocaleDateString('en-US', { month: 'short' })}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Time Slots */}
            {selectedDate && (
              <div className="time-slots-section">
                <h3 className="time-slots-title">
                  Available Times
                  {(isCheckingAvailability || isLoadingAvailability) && (
                    <span style={{ fontSize: '0.9rem', marginLeft: '1rem', opacity: 0.7 }}>
                      Loading availability...
                    </span>
                  )}
                </h3>
                {isLoadingAvailability ? (
                  <div style={{ textAlign: 'center', padding: '2rem', color: '#C7D2FE' }}>
                    Loading available times...
                  </div>
                ) : availableTimeSlots.length === 0 ? (
                  <div style={{ 
                    textAlign: 'center', 
                    padding: '2rem', 
                    color: '#C7D2FE',
                    background: 'rgba(255, 255, 255, 0.05)',
                    borderRadius: '12px',
                    border: '1px solid rgba(255, 255, 255, 0.1)'
                  }}>
                    <p>No available time slots for this date. Please select another date.</p>
                  </div>
                ) : (
                  <div className="time-slots-grid">
                    {availableTimeSlots.map((time, index) => {
                      const isSelected = selectedTime === time;
                      const isBooked = bookedSlots.includes(time);
                      return (
                        <button
                          key={index}
                          type="button"
                          className={`time-slot-button ${isSelected ? 'selected' : ''} ${isBooked ? 'booked' : ''}`}
                          onClick={() => !isBooked && setSelectedTime(time)}
                          disabled={isBooked}
                          title={isBooked ? 'This time slot is already booked' : ''}
                        >
                          {time}
                          {isBooked && <span className="booked-badge">Booked</span>}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        </section>

        {/* Customer Information */}
        <section className="customer-info-section">
          <div className="container">
            <h2 className="section-title">Your Contact Information</h2>
            {error && (
              <div className="error-message" style={{ 
                background: 'rgba(239, 68, 68, 0.2)', 
                border: '1px solid #ef4444', 
                color: '#fff',
                padding: '1rem',
                borderRadius: '8px',
                marginBottom: '1.5rem'
              }}>
                {error}
              </div>
            )}
            <div className="customer-form-grid">
              <div className="form-group">
                <label htmlFor="customerName" className="form-label">
                  Full Name <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="customerName"
                  className="form-input"
                  placeholder="Enter your full name"
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="customerEmail" className="form-label">
                  Email <span className="required">*</span>
                </label>
                <input
                  type="email"
                  id="customerEmail"
                  className="form-input"
                  placeholder="Enter your email address"
                  value={customerEmail}
                  onChange={(e) => setCustomerEmail(e.target.value)}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="customerPhone" className="form-label">
                  Phone Number <span className="required">*</span>
                </label>
                <input
                  type="tel"
                  id="customerPhone"
                  className="form-input"
                  placeholder="Enter your phone number"
                  value={customerPhone}
                  onChange={(e) => setCustomerPhone(e.target.value)}
                  required
                />
              </div>
            </div>
          </div>
        </section>

        {/* User Notes */}
        <section className="user-notes-section">
          <div className="container">
            <h2 className="section-title">Your Intention (Optional)</h2>
            <textarea
              className="notes-textarea"
              placeholder="Share any focus areas, intentions, or notes you'd like us to know about..."
              value={userNotes}
              onChange={(e) => setUserNotes(e.target.value)}
              rows="4"
            />
          </div>
        </section>

        {/* Payment Section */}
        {showPayPal && !paymentApproved && (
          <section className="payment-section">
            <div className="container">
              <h2 className="section-title">Complete Payment</h2>
              <div className="payment-summary">
                <div className="payment-summary-row">
                  <span className="payment-label">Service:</span>
                  <span className="payment-value">{displayService.name}</span>
                </div>
                <div className="payment-summary-row">
                  <span className="payment-label">Date & Time:</span>
                  <span className="payment-value">{selectedDate && formatDate(selectedDate)} at {selectedTime}</span>
                </div>
                <div className="payment-summary-row payment-total">
                  <span className="payment-label">Total Amount:</span>
                  <span className="payment-value-total">{formatPrice(service.price)}</span>
                </div>
              </div>
              <div className="paypal-container">
                {isRejected && (
                  <div className="paypal-error">
                    <p style={{ color: '#ef4444', marginBottom: '1rem' }}>
                      Failed to load PayPal. Please refresh the page or try again later.
                    </p>
                    <button
                      type="button"
                      onClick={() => window.location.reload()}
                      className="btn-retry-paypal"
                    >
                      Retry
                    </button>
                  </div>
                )}
                {!paypalButtonsReady && isPending && !isRejected && (
                  <div className="paypal-loading">
                    <div className="loading-spinner" style={{ 
                      width: '40px', 
                      height: '40px', 
                      border: '4px solid rgba(124, 58, 237, 0.3)',
                      borderTopColor: '#7C3AED',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite',
                      margin: '0 auto 1rem'
                    }}></div>
                    <p>Loading PayPal...</p>
                  </div>
                )}
                {canRenderButtons && showPayPal && (
                  <div 
                    id="paypal-button-container" 
                    ref={paypalButtonContainerRef}
                    key={`paypal-buttons-${canRenderButtons}`}
                    style={{ minHeight: '50px' }}
                  >
                    {/* Buttons will be rendered here by useEffect */}
                  </div>
                )}
              </div>
              <button
                type="button"
                className="btn-cancel-payment"
                onClick={() => {
                  setShowPayPal(false);
                  setError('');
                }}
              >
                Cancel Payment
              </button>
            </div>
          </section>
        )}

        {/* Confirmation Notice */}
        {!showPayPal && (
          <section className="confirmation-notice-section">
            <div className="container">
              <div className="notice-content">
                <div className="notice-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM13 17H11V15H13V17ZM13 13H11V7H13V13Z" fill="currentColor"/>
                  </svg>
                </div>
                <div className="notice-text">
                  <p className="notice-main">
                    Complete your booking by proceeding to secure payment. Your session will be confirmed upon successful payment.
                  </p>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Call to Action */}
        {!showPayPal && (
          <section className="cta-section">
            <div className="container">
              <button 
                type="submit"
                className={`btn-request-booking ${!isFormValid || isSubmitting ? 'disabled' : ''}`}
                disabled={!isFormValid || isSubmitting}
              >
                {isSubmitting ? 'Processing...' : 'Proceed to Payment'}
              </button>
            </div>
          </section>
        )}

        {/* Processing Payment Message */}
        {paymentApproved && (
          <section className="cta-section">
            <div className="container">
              <div className="processing-message">
                <div className="loading-spinner" style={{ 
                  width: '40px', 
                  height: '40px', 
                  border: '4px solid rgba(124, 58, 237, 0.3)',
                  borderTopColor: '#7C3AED',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                  margin: '0 auto 1rem'
                }}></div>
                <p>Payment successful! Confirming your booking...</p>
              </div>
            </div>
          </section>
        )}

        {/* Trust Indicators */}
        <section className="trust-indicators-section">
          <div className="container">
            <p className="trust-text">
              Your information is kept private and secure. All sessions are conducted with 
              respect, care, and professional energy work practices.
            </p>
          </div>
        </section>
      </form>

      {/* Gentle Closure */}
      <footer className="book-session-footer">
        <div className="wave-footer-closure"></div>
      </footer>
    </div>
  );
}

export default BookSession;

